import {
  IOfferingRes,
  IProduct,
  IRateResSchema,
  OfferingTypesEnum,
  RateBillingFrequencyEnum,
  RateUsageBillingFrequencyEnum,
  SubscriptionTimingEnum,
} from '@monetize/types/app';
import { Maybe } from '@monetize/types/common';
import { RefObject } from 'react';
import { MdExpandMore } from 'react-icons/md';
import {
  MAccordionButton,
  MAccordionPanel,
  MAlert,
  MBox,
  MButton,
  MDivider,
  MFlex,
  MGrid,
  MIcon,
  MText,
  MTooltip,
} from '../../../../../components/Monetize';
import { RateFormHeader } from './RateFormHeader';
import RatePrices from './RatePrices';
import { RateSettingsPopover } from './RateSettingsPopover';

const gridTemplateExpanded =
  '0.625rem 18.75rem 8.875rem 8.875rem minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) 2.6rem';

interface RateFormExpandedProps {
  // Rate data
  offeringRate: IRateResSchema | null;

  // Configuration
  type: OfferingTypesEnum;
  products: IProduct[];
  offering?: IOfferingRes;

  // Form values
  billingFrequency: Maybe<RateBillingFrequencyEnum>;
  billingFrequencyInMonths: Maybe<number>;
  subscriptionTiming?: SubscriptionTimingEnum | null;
  usageBillingFrequency?: RateUsageBillingFrequencyEnum | null;
  currency: string;

  // State flags
  isLocked: boolean;
  isReadOnly?: boolean;
  isSubscription: boolean;
  isSubscriptionProductTypeExist: boolean;
  isCreditAllocationFrequencyRequired: boolean;
  archivedOffering?: boolean;
  loading: boolean;

  // Handlers
  handleReset: () => void;
  handleClone: () => void;
  handleRemove: () => Promise<void>;
  handleSaveOrUpdate: () => void;
  handleSavePlusCreateOtherFrequencies: () => Promise<void>;

  // Refs and state
  nameRef: RefObject<HTMLInputElement | null>;
  defaultCurrency: string;
  pricesToCopyFromProductId?: string;
  setPricesToCopyFromProductId: (productId: string | undefined) => void;

  // Additional state
  showUnsavedAlert: boolean;
  isDirty: boolean;
}

export const RateFormExpanded = ({
  offeringRate,
  type,
  products,
  offering,
  billingFrequency,
  billingFrequencyInMonths,
  subscriptionTiming,
  usageBillingFrequency,
  currency,
  isLocked,
  isReadOnly,
  isSubscription,
  isSubscriptionProductTypeExist,
  isCreditAllocationFrequencyRequired,
  archivedOffering,
  loading,
  handleReset,
  handleClone,
  handleRemove,
  handleSaveOrUpdate,
  handleSavePlusCreateOtherFrequencies,
  nameRef,
  defaultCurrency,
  pricesToCopyFromProductId,
  setPricesToCopyFromProductId,
  showUnsavedAlert,
  isDirty,
}: RateFormExpandedProps) => {
  return (
    <>
      <MGrid
        gridTemplateColumns={gridTemplateExpanded}
        gap={4}
        py={4}
        pl={2.5}
        pr={4}
      >
        <MTooltip label="Toggle" placement="top-start" contentProps={{ mt: 4 }}>
          <MAccordionButton
            p={0}
            alignItems="flex-start"
            _hover={{ bg: 'white' }}
            data-testid="toggle-rate-button"
          >
            <MIcon as={MdExpandMore} boxSize="1.5rem" />
          </MAccordionButton>
        </MTooltip>
        <RateFormHeader
          rateId={offeringRate?.id || ''}
          loading={loading}
          archivedOffering={archivedOffering}
          nameRef={nameRef}
          currency={currency}
          isLocked={isLocked}
          type={type}
          billingFrequency={billingFrequency}
          billingFrequencyInMonths={billingFrequencyInMonths}
          subscriptionTiming={subscriptionTiming}
          usageBillingFrequency={usageBillingFrequency}
          handleReset={handleReset}
          handleClone={handleClone}
          handleRemove={handleRemove}
          isReadOnly={isReadOnly}
          isSubscriptionProductTypeExist={isSubscriptionProductTypeExist}
          isSubscription={isSubscription}
          isCreditAllocationFrequencyRequired={
            isCreditAllocationFrequencyRequired
          }
        />
      </MGrid>

      <MAccordionPanel py={0} pl={8}>
        <MBox px="4">
          <MDivider borderColor="tBlue.hover" />
        </MBox>
        <MBox
          px="4"
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mt="4"
        >
          <MText fontSize="lg" fontWeight="bold">
            Product Prices
          </MText>
          <RateSettingsPopover
            offering={offering}
            products={products}
            handleSave={handleSaveOrUpdate}
          />
        </MBox>
        {products.map((product, index: number) => (
          <MFlex flexDir="column" key={`${product.id}-${index}`}>
            <RatePrices
              ref={nameRef}
              product={product}
              key={product.id}
              isLocked={isLocked || !!archivedOffering}
              loading={loading}
              isLastRow={products.length - 1 === index}
              currency={offeringRate?.currency || defaultCurrency}
              pricesLengthFromApi={offeringRate?.prices?.length}
              isExistingRate={!!offeringRate?.id}
              isReadOnly={isReadOnly}
              offeringType={type}
              pricesToCopyFromProductId={pricesToCopyFromProductId}
              isMultipleProduct={products.length > 1}
              setPricesToCopyFromProductId={setPricesToCopyFromProductId}
              isProductExpanded={true}
            />
            {products.length - 1 !== index && (
              <MDivider borderColor="tBlue.hover" opacity="1" />
            )}
          </MFlex>
        ))}

        <MDivider borderColor="tBlue.hover" opacity="1" />
        {!isReadOnly && (
          <MFlex align="center" justify="flex-end" gap={4} py={4}>
            {showUnsavedAlert && (
              <MAlert
                type="warning"
                alertProps={{ px: 2, py: 1 }}
                alertIconProps={{ width: 4, height: 4 }}
                alertDescriptionProps={{ fontSize: 'sm' }}
              >
                Unsaved Changes
              </MAlert>
            )}
            <MButton
              variant="primary"
              isLoading={loading}
              isDisabled={loading || !isDirty || archivedOffering}
              onClick={handleSaveOrUpdate}
              type="submit"
              data-testid="rate-save-btn"
            >
              Save
            </MButton>
            {!offeringRate?.id && type !== OfferingTypesEnum.ONETIME && (
              <MButton
                variant="primary"
                isLoading={loading}
                isDisabled={loading || !isDirty || archivedOffering}
                onClick={handleSavePlusCreateOtherFrequencies}
              >
                Save + Create Other Frequencies
              </MButton>
            )}
          </MFlex>
        )}
      </MAccordionPanel>
    </>
  );
};
